import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Divider, Drawer, InputNumber, Radio, Segmented, Select, Space, Switch, Tag, Typography, message } from 'antd';
import { SafetyCertificateOutlined, SaveOutlined, EditOutlined } from '@ant-design/icons';
import {
  saveActionPolicy,
  signActionPolicy,
  type ActionPolicyRow,
  type ActionPolicyVersion,
  type ActionPolicyView,
  type ActionTier,
} from '@/api/actionPolicies';
import { StatusPill } from '@/components/StatusPill';
import { colors } from '@/theme/tokens';

const { Text, Paragraph } = Typography;

type Scope = 'platform' | 'practice';

interface Props {
  open: boolean;
  row: ActionPolicyRow | null;
  view: ActionPolicyView | null;
  canEdit: boolean;
  merchantId: number | null;
  merchantName: string | null;
  onClose: () => void;
  onChanged: () => void;
}

const fmt = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '');

/**
 * Edit one action's rule — the platform default or the selected practice's override — and sign the
 * saved version. A signature belongs to exactly what was approved: saving a change clears it, so the
 * Sign button only ever signs what is stored, never unsaved edits.
 */
export const ActionPolicyDrawer = observer(function ActionPolicyDrawer({
  open, row, view, canEdit, merchantId, merchantName, onClose, onChanged,
}: Props) {
  const [scope, setScope] = useState<Scope>('platform');
  const current: ActionPolicyVersion | null | undefined = scope === 'platform' ? row?.platform : row?.practice;
  const [tier, setTier] = useState<ActionTier | null>(null);
  const [role, setRole] = useState<number | null>(null);
  const [undo, setUndo] = useState<number | null>(null);
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !row) return;
    setScope(merchantId && row.practice ? 'practice' : 'platform');
  }, [open, row, merchantId]);

  useEffect(() => {
    setTier((current?.tier ?? null) as ActionTier | null);
    setRole(current?.deciderRole ?? null);
    setUndo(current?.undoWindowMinutes ?? null);
    setActive(current?.isActive ?? true);
  }, [current, scope]);

  const dirty = useMemo(() => {
    if (tier === null) return false;
    if (!current) return true;
    return current.tier !== tier || (current.deciderRole ?? null) !== role
      || (current.undoWindowMinutes ?? null) !== (tier === 1 ? undo : null) || current.isActive !== active;
  }, [current, tier, role, undo, active]);

  if (!row || !view) return null;
  const targetMerchant = scope === 'platform' ? 0 : merchantId ?? 0;

  async function save() {
    if (tier === null || !row) return;
    setBusy(true);
    try {
      await saveActionPolicy({
        merchantId: targetMerchant, kind: row.kind, tier,
        deciderRole: tier === 3 ? role : role ?? null,
        undoWindowMinutes: tier === 1 ? undo : null,
        isActive: active,
      });
      message.success('Saved. This version is not signed yet.');
      onChanged();
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  async function sign() {
    if (!current || !row) return;
    setBusy(true);
    try {
      await signActionPolicy(targetMerchant, row.kind, current.version);
      message.success(`Signed version ${current.version}.`);
      onChanged();
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Could not sign');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={560}
      title={
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 16 }}>{row.label}</Text>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>{row.kind}</Text>
        </Space>
      }
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {row.isChatTool ? (
          <Text type="secondary" style={{ fontSize: 12.5 }}>
            Called by agents as {row.tools.map((t) => <Tag key={t} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{t}</Tag>)}
          </Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 12.5 }}>A clinical request the care team sends — not an action an agent takes in chat.</Text>
        )}

        <Segmented<Scope>
          block
          value={scope}
          onChange={setScope}
          options={[
            { label: 'Platform default (every practice)', value: 'platform' },
            { label: merchantId ? `${merchantName ?? 'This practice'} only` : 'Pick a practice in the header', value: 'practice', disabled: !merchantId },
          ]}
        />
        {scope === 'practice' && !row.practice && (
          <Alert type="info" showIcon message={`${merchantName ?? 'This practice'} uses the platform default for this action. Saving here creates its own rule.`} />
        )}
        {scope === 'platform' && (
          <Alert type="warning" showIcon message="The platform default applies to every practice that has no rule of its own." />
        )}

        <div>
          <Text strong>What should happen</Text>
          <Radio.Group value={tier} onChange={(e) => setTier(e.target.value)} disabled={!canEdit} style={{ width: '100%', marginTop: 8 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {view.tiers.map((t) => (
                <Radio key={t.value} value={t.value} style={{ alignItems: 'flex-start' }}>
                  <div>
                    <Text strong>{t.name}</Text>
                    {row.isChatTool && <div style={{ fontSize: 12, color: colors.slate[500] }}>Today: {t.chatToday}</div>}
                  </div>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </div>

        {tier === 3 && (
          <div>
            <Text strong>Which role approves</Text>
            <Select style={{ width: '100%', marginTop: 6 }} value={role ?? undefined} onChange={setRole} disabled={!canEdit}
              placeholder="Choose a role" options={view.roles.map((r) => ({ value: r.value, label: r.name }))} />
          </div>
        )}
        {tier === 1 && (
          <div>
            <Text strong>Undo window (minutes)</Text>
            <InputNumber style={{ width: '100%', marginTop: 6 }} min={1} max={1440} value={undo ?? undefined} onChange={(v) => setUndo(v ?? null)} disabled={!canEdit} />
          </div>
        )}
        <Space>
          <Switch checked={active} onChange={setActive} disabled={!canEdit} />
          <Text>{active ? 'In force' : 'Switched off (this rule is ignored)'}</Text>
        </Space>

        {canEdit ? (
          <Button type="primary" icon={<SaveOutlined />} onClick={() => void save()} loading={busy} disabled={!dirty}>
            {current ? 'Save changes' : 'Create rule'}
          </Button>
        ) : (
          <Alert type="info" showIcon icon={<EditOutlined />} message="Only an Agent Studio admin can change or sign action policies." />
        )}

        <Divider style={{ margin: '4px 0' }} />

        <div>
          <Text strong>Sign-off</Text>
          {!current ? (
            <Paragraph type="secondary" style={{ marginTop: 6 }}>No rule saved here yet — nothing to sign.</Paragraph>
          ) : current.signed ? (
            <Paragraph style={{ marginTop: 6 }}>
              <StatusPill tone="green" icon={<SafetyCertificateOutlined />}>Signed</StatusPill>{' '}
              Version {current.version}, signed by <b>{current.signedByName}</b> on {fmt(current.signedAtUtc)}.
            </Paragraph>
          ) : (
            <Space direction="vertical" style={{ marginTop: 6, width: '100%' }}>
              <Text>
                <StatusPill tone="amber">Not signed</StatusPill>{' '}
                Version {current.version}{current.changedAtUtc ? `, last changed ${fmt(current.changedAtUtc)}` : ''}. Unsigned rules still apply.
              </Text>
              {canEdit && (
                <Button icon={<SafetyCertificateOutlined />} onClick={() => void sign()} loading={busy} disabled={dirty}>
                  Sign version {current.version}
                </Button>
              )}
              {canEdit && dirty && <Text type="secondary" style={{ fontSize: 12 }}>Save your changes first — a signature covers the saved version only.</Text>}
            </Space>
          )}
        </div>
      </Space>
    </Drawer>
  );
});
